import { CALL_SLOT_STATE } from "./callStates.js";
import { CALL_DIRECTION, CALL_ROLE } from "./callDirections.js";
import { SIGNALING_CONNECTION_STATE } from "./signalingTypes.js";
import { MEDIA_CAPTURE_STATE } from "./mediaTypes.js";
import { RTC_CONNECTION_STATE } from "./rtcTypes.js";
import { CALL_STATE as SCS } from "../server-contracts/callStates.js";
/**
 * Active call access
 */
export function selectHasActiveCall(model) {
    return model.callSlotState === CALL_SLOT_STATE.ACTIVE && model.activeCall !== null;
}
export function selectActiveCall(model) {
    return model.callSlotState === CALL_SLOT_STATE.ACTIVE ? model.activeCall : null;
}
export function selectActiveCallId(model) {
    return selectActiveCall(model)?.callId ?? null;
}
export function selectServerCallState(model) {
    return selectActiveCall(model)?.serverCallState ?? null;
}
export function selectRemoteUserId(model) {
    return selectActiveCall(model)?.remoteUserId ?? null;
}
/**
 * Role / direction
 */
export function selectCallDirection(model) {
    return selectActiveCall(model)?.direction ?? CALL_DIRECTION.NONE;
}
export function selectCallRole(model) {
    return selectActiveCall(model)?.selfRole ?? CALL_ROLE.NONE;
}
export function selectIsOutgoingCall(model) {
    return selectCallDirection(model) === CALL_DIRECTION.OUTGOING;
}
export function selectIsIncomingCall(model) {
    return selectCallDirection(model) === CALL_DIRECTION.INCOMING;
}
export function selectIsCaller(model) {
    return selectCallRole(model) === CALL_ROLE.CALLER;
}
export function selectIsCallee(model) {
    return selectCallRole(model) === CALL_ROLE.CALLEE;
}
/**
 * Signaling
 */
export function selectIsSignalingConnected(model) {
    return (model.signalingConnectionState === SIGNALING_CONNECTION_STATE.CONNECTED ||
        model.signalingConnectionState === SIGNALING_CONNECTION_STATE.AUTHENTICATED ||
        model.signalingConnectionState === SIGNALING_CONNECTION_STATE.READY);
}
export function selectIsSignalingReady(model) {
    return model.signalingConnectionState === SIGNALING_CONNECTION_STATE.READY;
}
export function selectIsSignalingAuthenticated(model) {
    return (model.signalingConnectionState === SIGNALING_CONNECTION_STATE.AUTHENTICATED ||
        model.signalingConnectionState === SIGNALING_CONNECTION_STATE.READY);
}
export function selectIsSignalingReconnecting(model) {
    return model.signalingConnectionState === SIGNALING_CONNECTION_STATE.RECONNECTING;
}
/**
 * Call lifecycle predicates
 */
export function selectIsRinging(model) {
    return selectServerCallState(model) === SCS.RINGING;
}
export function selectIsNegotiating(model) {
    return selectServerCallState(model) === SCS.NEGOTIATING;
}
export function selectIsInCall(model) {
    return selectServerCallState(model) === SCS.IN_CALL;
}
export function selectIsCallReconnecting(model) {
    return selectServerCallState(model) === SCS.RECONNECTING;
}
export function selectIsCallEnded(model) {
    return selectServerCallState(model) === SCS.ENDED;
}
/**
 * Common UI/controller predicates
 */
export function selectCanCreateCall(model) {
    return (model.callSlotState === CALL_SLOT_STATE.IDLE &&
        selectIsSignalingAuthenticated(model));
}
export function selectCanAcceptCall(model) {
    return (selectHasActiveCall(model) &&
        selectIsIncomingCall(model) &&
        selectIsRinging(model));
}
export function selectCanRejectCall(model) {
    return (selectHasActiveCall(model) &&
        selectIsIncomingCall(model) &&
        selectIsRinging(model));
}
export function selectCanEndCall(model) {
    const state = selectServerCallState(model);
    const activeCall = selectActiveCall(model);
    const localUserId = model.session?.userId ?? null;
    const localSessionId = model.session?.sessionId ?? null;

    let canControlFromThisSession = true;
    if (activeCall && localUserId && localSessionId) {
        const participant = activeCall.participants?.[localUserId] ?? null;
        const owningSessionId = participant?.owningSessionId ?? null;
        if (owningSessionId && owningSessionId !== localSessionId) {
            canControlFromThisSession = false;
        }
    }

    return (selectHasActiveCall(model) &&
        canControlFromThisSession &&
        state !== null &&
        state !== SCS.ENDED);
}
export function selectShouldShowIncomingRinging(model) {
    return selectCanAcceptCall(model);
}
export function selectShouldShowOutgoingRinging(model) {
    return (selectHasActiveCall(model) &&
        selectIsOutgoingCall(model) &&
        selectIsRinging(model));
}
export function selectShouldShowNegotiating(model) {
    return selectHasActiveCall(model) && selectIsNegotiating(model);
}
export function selectShouldShowReconnectBanner(model) {
    return selectIsSignalingReconnecting(model) || selectIsCallReconnecting(model);
}
/**
 * Media
 */
export function selectHasLocalStream(model) {
    return model.media.hasLocalStream;
}
export function selectIsLocalMediaReady(model) {
    return model.media.captureState === MEDIA_CAPTURE_STATE.READY;
}
export function selectIsLocalMediaAcquiring(model) {
    return model.media.captureState === MEDIA_CAPTURE_STATE.ACQUIRING;
}
export function selectIsLocalAudioEnabled(model) {
    return model.media.audio.enabled;
}
export function selectIsLocalVideoEnabled(model) {
    return model.media.video.enabled;
}
export function selectIsSpeaking(model) {
    return model.media.isSpeaking;
}
/**
 * RTC
 */
export function selectRtcConnectionState(model) {
    return model.rtc.connectionState;
}
export function selectIsRtcConnected(model) {
    return model.rtc.connectionState === RTC_CONNECTION_STATE.CONNECTED;
}
export function selectHasRemoteMedia(model) {
    return model.rtc.remoteMedia.hasRemoteStream;
}
export function selectHasRemoteVideo(model) {
    return model.rtc.remoteMedia.hasRemoteVideoTrack;
}
export function selectHasRemoteAudio(model) {
    return model.rtc.remoteMedia.hasRemoteAudioTrack;
}
/**
 * Errors
 */
export function selectHasSignalingError(model) {
    return model.error !== null;
}
export function selectHasMediaError(model) {
    return model.media.error !== null;
}
export function selectHasRtcError(model) {
    return model.rtc.error !== null;
}
export function selectAnyError(model) {
    return (selectHasSignalingError(model) ||
        selectHasMediaError(model) ||
        selectHasRtcError(model));
}
